import React, { useRef, useState } from "react";
import { ChevronLeft, ChevronRight, DollarSign, TrendingUp, TrendingDown } from "lucide-react";

const formatAmount = (amount) => {
  if (amount >= 1000) return `$${(amount / 1000).toFixed(2).replace(/\.?0+$/, '')}K`;
  return `$${amount.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
};

const InvoiceMonthlyCards = ({ months = [], isEstimate = false, isDark }) => {
  const scrollRef = useRef(null);
  const [selectedIdx, setSelectedIdx] = useState(0);

  const scroll = (dir) => {
    if (!scrollRef.current) return;
    scrollRef.current.scrollBy({ left: dir * 180, behavior: "smooth" });
  };

  const accent = isEstimate ? "#f97316" : "#3b82f6";

  if (!months.length) return null;

  return (
    <div className="relative group" data-testid="monthly-cards">
      <button
        onClick={() => scroll(-1)}
        className={`absolute left-0 top-1/2 -translate-y-1/2 z-10 w-8 h-8 rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity shadow-lg ${
          isDark ? "bg-zinc-800 text-white hover:bg-zinc-700" : "bg-white text-gray-700 hover:bg-gray-50 border border-gray-200"
        }`}
        data-testid="monthly-scroll-left"
      >
        <ChevronLeft className="w-4 h-4" />
      </button>

      <div ref={scrollRef} className="flex gap-3 overflow-x-auto scrollbar-hide pb-1 px-1" style={{ scrollbarWidth: "none", msOverflowStyle: "none" }}>
        {months.map((m, i) => {
          const isSelected = i === selectedIdx;
          return (
            <button
              key={m.label}
              onClick={() => setSelectedIdx(i)}
              className={`flex-shrink-0 rounded-xl p-3.5 w-[140px] text-left transition-all duration-200 ${
                isSelected
                  ? isDark
                    ? "ring-2 ring-offset-1 ring-offset-zinc-900 bg-zinc-800/80 shadow-lg"
                    : "ring-2 ring-offset-1 ring-offset-white bg-white shadow-lg"
                  : isDark
                    ? "bg-zinc-800/40 hover:bg-zinc-800/60"
                    : "bg-white/70 hover:bg-white border border-gray-100"
              }`}
              style={isSelected ? { ringColor: accent } : {}}
              data-testid={`monthly-card-${i}`}
            >
              <div className="flex items-center justify-between mb-1.5">
                <span className={`text-xs font-medium ${isDark ? "text-zinc-400" : "text-gray-500"}`}>{m.label}</span>
                <div className="w-6 h-6 rounded-full flex items-center justify-center" style={{ backgroundColor: `${accent}20` }}>
                  <DollarSign className="w-3 h-3" style={{ color: accent }} />
                </div>
              </div>
              <div className={`text-lg font-bold mb-1 ${isDark ? "text-white" : "text-gray-900"}`}>
                {formatAmount(m.total)}
              </div>
              {m.pct_change !== 0 && (
                <div className={`flex items-center gap-0.5 text-[11px] font-medium ${
                  m.pct_change > 0 ? "text-emerald-500" : "text-red-500"
                }`}>
                  {m.pct_change > 0 ? <TrendingUp className="w-3 h-3" /> : <TrendingDown className="w-3 h-3" />}
                  {m.pct_change > 0 ? "+" : ""}{m.pct_change}%
                </div>
              )}
            </button>
          );
        })}
      </div>

      <button
        onClick={() => scroll(1)}
        className={`absolute right-0 top-1/2 -translate-y-1/2 z-10 w-8 h-8 rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity shadow-lg ${
          isDark ? "bg-zinc-800 text-white hover:bg-zinc-700" : "bg-white text-gray-700 hover:bg-gray-50 border border-gray-200"
        }`}
        data-testid="monthly-scroll-right"
      >
        <ChevronRight className="w-4 h-4" />
      </button>
    </div>
  );
};

export default InvoiceMonthlyCards;
